/**
 * In-context cadence — which registry source type_codes each module may
 * cadence against. Drives the module-embedded <CadenceRuleList allowedTypeCodes>
 * (subfiltered list + create-form type picker). The standalone Cadence module
 * passes nothing and shows ALL.
 */
export const CADENCE_MODULE_TYPES: Record<string, string[]> = {
  risk:         ['RISK_ASSESSMENT_TEMPLATE'],
  control:      ['CONTROL_TEST_TEMPLATE'],
  audit:        ['AUDIT_PROGRAM'],
  governance:   ['FRAMEWORK', 'SOP', 'CONTRACT', 'LAW', 'TEST_OF_COMPLIANCE', 'TEST_OF_DESIGN', 'MEETING'],
  organization: ['APPLICATION', 'IT_ASSET', 'USER', 'POSITION', 'ROLE', 'LICENSE'],
  // FILING (COM_filings) is the live Obligation Register and the only kind the
  // picker offers. OBLIGATION (COM_register) is retired — kept in this list only
  // so rules created against it before the retirement still show in the tab.
  compliance:   ['FILING', 'OBLIGATION'],
  project:      ['PROJECT'],
  itAssessment: ['IT_ASSESSMENT_TEMPLATE'],
  performance:  ['PERFORMANCE_FORM'],
};

/**
 * RBAC (extent-only) — the per-module cadence activity key that gates a given
 * in-context CadenceRuleList. Keyed by the same module slug as
 * CADENCE_MODULE_TYPES. The standalone Cadence Engine view (no allowedTypeCodes)
 * is gated by SCH_GLOBAL_CADENCE instead.
 */
export const CADENCE_MODULE_ACTIVITY: Record<string, string> = {
  risk:         'SCH_RSK_CADENCE',
  control:      'SCH_CTR_CADENCE',
  governance:   'SCH_GOV_CADENCE',
  audit:        'SCH_ADT_CADENCE',
  compliance:   'SCH_COM_CADENCE',
  project:      'SCH_PRM_CADENCE',
  // Each branch had added one of these two and not the other; both are real
  // modules, so both are kept.
  performance:  'SCH_PFM_CADENCE',
  organization: 'SCH_ORG_CADENCE',
};

export const SCH_GLOBAL_CADENCE = 'SCH_GLOBAL_CADENCE';

/**
 * Resolve the cadence activity key a CadenceRuleList should gate on, from its
 * `allowedTypeCodes`. Returns SCH_GLOBAL_CADENCE only when standalone (no codes)
 * — or when the codes map to no per-module activity. organization now has its
 * own key (SCH_ORG_CADENCE, seeded 2026-09-14). itAssessment still has none and
 * falls back to SCH_GLOBAL_CADENCE; give it CTR's key or its own when the ITAT
 * cadence tab is settled.
 */
export function cadenceActivityForTypeCodes(allowedTypeCodes?: string[]): string {
  if (!allowedTypeCodes || allowedTypeCodes.length === 0) return SCH_GLOBAL_CADENCE;
  for (const [slug, codes] of Object.entries(CADENCE_MODULE_TYPES)) {
    const act = CADENCE_MODULE_ACTIVITY[slug];
    if (act && allowedTypeCodes.some(c => codes.includes(c))) return act;
  }
  return SCH_GLOBAL_CADENCE;
}
